/**
 * Print through the platform's own panel, in this window.
 *
 * `window.open` plus `print()` is a browser download-adjacent trick: it needs
 * a popup, and in a Tauri webview that popup often never appears. A hidden
 * iframe still calls `window.print()`, which WKWebView maps onto the native
 * macOS print sheet, and the website gets the same panel without fighting a
 * pop-up blocker.
 */

export function printHtml(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    const frameDocument = iframe.contentDocument;
    if (!frameWindow || !frameDocument) {
      iframe.remove();
      reject(new Error("Could not open the print view."));
      return;
    }

    const cleanup = () => {
      window.setTimeout(() => iframe.remove(), 500);
      resolve();
    };

    frameWindow.addEventListener("afterprint", cleanup);
    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();

    const run = () => {
      try {
        frameWindow.focus();
        frameWindow.print();
      } catch (error) {
        iframe.remove();
        reject(error);
      }
    };

    // Scripts in the document (slide fitting, image decode) need a beat
    // after load. The manuscript path has nothing to wait for; 400ms is
    // the same delay the old popup used, so decks that fitted there fit here.
    const kick = () => window.setTimeout(run, 400);
    if (frameDocument.readyState === "complete") kick();
    else iframe.addEventListener("load", kick, { once: true });
  });
}

export function escapePrintHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function manuscriptPrintHtml(title: string, body: string): string {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapePrintHtml(title)}</title>
<style>
  body { font-family: Georgia, "Times New Roman", serif; line-height: 1.6; margin: 2.5cm; color: #1a1c22; }
  h1 { font-size: 22pt; margin: 0 0 1cm; }
  h2 { font-size: 16pt; margin: 0.8cm 0 0.3cm; }
  h3 { font-size: 13pt; margin: 0.6cm 0 0.2cm; }
  p { margin: 0 0 0.45cm; }
  blockquote { border-left: 3px solid #ccc; padding-left: 0.8cm; margin: 0 0 0.45cm; color: #444; }
  ul, ol { margin: 0 0 0.45cm; padding-left: 1cm; }
  code { background: #f4f4f4; padding: 1px 4px; border-radius: 3px; font-size: 0.9em; }
  pre { background: #f4f4f4; padding: 0.4cm; border-radius: 4px; overflow-x: auto; }
  @media print { a { color: inherit; text-decoration: none; } }
</style>
</head>
<body>
<h1>${escapePrintHtml(title)}</h1>
${body}
</body>
</html>`;
}
